'use client'

import Link from 'next/link'
import { useState } from 'react'

import { IconPause, IconPlay, IconSpeakerHigh, IconSpeakerMuted } from '@/app/components/atoms/Icons'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { FALLBACK_IMAGES } from '@/app/lib/constants/fallbackImages'
import { getYouTubeThumbnailUrl } from '@/app/lib/utils/youtube'

import { useActiveVideoPlayer } from '../hooks/useActiveVideoPlayer'
import styles from './VideoCard.module.scss'
import type { VideoCardProps } from './VideoCard.types'

/** Escala de la tarjeta según su distancia a la activa — valores exactos del
 * carrusel de referencia (`EventosDestacados`: `rel === 0 ? 1 : abs === 1 ?
 * 0.78 : 0.6`). */
const SCALE_BY_ABS: Record<number, number> = { 0: 1, 1: 0.78, 2: 0.6 }

/** Desplazamiento horizontal por posición, en px fijos — valor exacto de la
 * referencia (`tx = rel * 210`). Fijo, no relativo al ancho de la tarjeta:
 * la tarjeta también es de ancho fijo (ver `VideoCard.module.scss`), así que
 * la separación relativa se mantiene idéntica en todos los breakpoints y el
 * excedente se recorta contra el `overflow: clip` de la sección, igual que
 * en la referencia. */
const OFFSET_PX_PER_STEP = 210

/** Vista previa de una tarjeta inactiva — prioridad imagen 4:5 del CMS →
 * miniatura de YouTube → imagen de respaldo alternada. */
type PreviewStage =
  | { kind: 'cms-image'; src: string }
  | { kind: 'youtube-thumbnail'; src: string }
  | { kind: 'fallback-image'; src: string }

function firstStage(previewImageUrl: string | null, videoId: string): PreviewStage {
  return previewImageUrl
    ? { kind: 'cms-image', src: previewImageUrl }
    : { kind: 'youtube-thumbnail', src: getYouTubeThumbnailUrl(videoId) }
}

/**
 * Tarjeta individual del carrusel `Video` — posicionada de forma absoluta
 * según `rel` (activa centrada, hasta 2 asomando a cada lado), fiel al
 * carrusel de referencia (`EventosDestacados`, Figma "Vídeo"). La tarjeta
 * activa monta el único reproductor real del carrusel
 * (`useActiveVideoPlayer`) con controles de reproducir/pausar y silenciar/
 * activar sonido; las demás muestran su vista previa de imagen, sin montar
 * nunca un reproductor (FR-013), y son clicables para pasar a ser la
 * activa.
 */
export default function VideoCard({
  item,
  index,
  rel,
  isActive,
  hidden,
  apiReady,
  isInView,
  isMuted,
  onToggleMute,
  onSelect,
  onError,
  onEnded,
}: VideoCardProps) {
  const [stage, setStage] = useState<PreviewStage>(() =>
    firstStage(item.previewImageUrl, item.videoId),
  )
  const abs = Math.abs(rel)

  const handleImageError = () => {
    setStage((current) => {
      if (current.kind === 'cms-image') {
        return { kind: 'youtube-thumbnail', src: getYouTubeThumbnailUrl(item.videoId) }
      }
      if (current.kind === 'youtube-thumbnail') {
        const fallback = FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]
        return fallback ? { kind: 'fallback-image', src: fallback } : current
      }
      return current
    })
  }

  const { containerRef, isPlaying, isReady, togglePlay } = useActiveVideoPlayer({
    isActive,
    apiReady,
    isInView,
    videoId: item.videoId,
    muted: isMuted,
    onError: () => onError(item.id),
    onEnded,
  })

  // Colapsada al centro (posición/escala 0) y por detrás de la activa
  // mientras exceda los huecos que asoman — ni una posición ni un `z-index`
  // propios, así que al recuperar un hueco visible crece y se desliza desde
  // detrás de la pila en vez de aparecer de golpe (y, a la inversa, la que
  // deja de asomar se encoge hacia el centro en vez de desaparecer sin más).
  const scale = hidden ? 0 : (SCALE_BY_ABS[abs] ?? SCALE_BY_ABS[2])
  const translateX = hidden ? 0 : rel * OFFSET_PX_PER_STEP
  const zIndex = hidden ? -1 : 10 - abs
  const style = {
    transform: `translate(-50%, -50%) translateX(${translateX}px) scale(${scale})`,
    zIndex,
  }

  return (
    <div
      className={styles.card}
      data-active={isActive ? '' : undefined}
      data-item-id={item.id}
      aria-hidden={hidden || undefined}
      style={hidden ? { ...style, pointerEvents: 'none' as const } : style}
    >
      <div className={styles.bg} data-inactive={isActive ? undefined : ''}>
        {/* eslint-disable-next-line @next/next/no-img-element -- imagen remota del CMS/YouTube, no conocida en build time */}
        <img
          src={stage.src}
          alt=""
          loading={isActive ? 'eager' : 'lazy'}
          className={styles.previewImg}
          onError={handleImageError}
        />
      </div>

      <div
        className={styles.player}
        data-ready={isActive && isReady ? '' : undefined}
        ref={containerRef}
        aria-hidden={!isActive}
      />

      <div className={styles.overlay} aria-hidden="true" />

      {isActive && (
        <>
          {/* Clic en cualquier punto de la tarjeta activa (fuera de los
              controles/CTA, que van encima y ya paran la propagación) para
              reproducir/pausar — misma acción que el botón dedicado de
              `.controls`, solo que a tamaño completo. */}
          <button
            type="button"
            className={styles.toggleOverlay}
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pausar vídeo' : 'Reproducir vídeo'}
          />

          <div className={styles.controls}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                togglePlay()
              }}
              aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
              className={styles.iconBtn}
            >
              {isPlaying ? <IconPause size={16} /> : <IconPlay size={16} />}
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onToggleMute()
              }}
              aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
              className={styles.iconBtn}
            >
              {isMuted ? <IconSpeakerMuted size={20} /> : <IconSpeakerHigh size={20} />}
            </button>
          </div>

          {item.priceDisplay?.tag && (
            <div className={styles.badgeCorner}>
              <PriceTag
                tag={item.priceDisplay.tag}
                hasAppPromotions={item.priceDisplay.hasAppPromotions}
              />
            </div>
          )}

          {(item.dateText || item.label || item.city || item.priceDisplay || item.purchaseUrl) && (
            <div className={styles.info}>
              {(item.dateText || item.label || item.city) && (
                <div className={styles.meta}>
                  {item.dateText && <span className={styles.dateText}>{item.dateText}</span>}
                  {item.label && <span className={styles.eventTitle}>{item.label}</span>}
                  {item.city && <span className={styles.city}>{item.city}</span>}
                </div>
              )}

              {item.priceDisplay && (
                <Price
                  amount={item.priceDisplay.amount}
                  isFree={item.priceDisplay.isFree}
                  originalAmount={item.priceDisplay.originalAmount ?? undefined}
                  variant={item.priceDisplay.originalAmount != null ? 'offer' : 'default'}
                  showFrom
                  inverse
                  accentColor
                />
              )}

              {item.purchaseUrl && (
                <Link href={item.purchaseUrl} className={styles.buyButton}>
                  Comprar
                </Link>
              )}
            </div>
          )}
        </>
      )}

      {/* Botón real (operable por teclado) que activa esta tarjeta — solo
          existe mientras no es la activa. El envoltorio exterior (`.card`,
          arriba) es SIEMPRE un `<div>`, tanto activa como no: si el tipo de
          elemento cambiara según `isActive` (`<div>` ↔ `<button>`), React
          desmontaría y volvería a montar el nodo entero cada vez que una
          tarjeta cruza a/desde el centro, perdiendo el valor de `transform`
          anterior del que partir — la tarjeta "se reemplazaría" en su sitio
          en vez de deslizarse (ver contexto de esta corrección). */}
      {!isActive && !hidden && (
        <button
          type="button"
          className={styles.selectOverlay}
          onClick={onSelect}
          aria-label={item.label ? `Ver vídeo: ${item.label}` : 'Ver vídeo'}
        />
      )}
    </div>
  )
}
